import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import _without from 'lodash/without';
import { Task, TaskTimelineFrameEnum } from 'graphql-common';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import useSearchParam from '@lib/hooks/useSearchParam';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { logger } from '@lib/services/logger';
import Modal from '@lib/components/Modal/Modal';
import GenerateReportForm from 'components/GenerateReportForm/GenerateReportForm';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import { APP_URLS } from 'constants/urls';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { useLocation } from 'react-router-dom';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import TaskCompletionsModal from 'components/TaskCompletionsModal/TaskCompletionsModal';
import TasksTimelineStateCounters from './TasksTimelineStateCounters';
import useActions from './utils/useActions';
import { getAdditionalColumns } from './utils/getAdditionalColumns';
import mergeTasksWithTimeline from './utils/mergeTasksWithTimeline';
import getTimelineTableColumns from './utils/getTimelineTableColumns';
import TasksTimelineFilter from './TasksTimelineFilter';

function TasksTimeline() {
  const { pathname } = useLocation();
  const useModalManagementResult = useModalManagement([
    Modals.GenerateReportModal,
  ]);
  const {
    endDateParam,
    filterParams,
    onEndDateChange,
    onUpdateFilterParams,
    onViewChange,
    paginationState,
    periodEndParam,
    periodStartParam,
    resetAllFilterParams,
    resetFilterParams,
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    timelineData = [],
    timelineGroupBy = TaskTimelineFrameEnum.Year,
    timelineLoading,
    totalTasksCount,
    view,
    onReportCreateAction,
    taskTimelineReportCreateMutationResult,
    basePeriodStartParam,
    basePeriodEndParam,
    isReportPreparing,
    reportDefaultPeriod,
  } = useActions({ useModalManagementResult });
  const { t } = useTranslation();
  const [isFullTableView, setFullTableView] = useState(false);
  const [searchQuery, setSearchQuery] = useSearchParam();
  const [selectedCell, setSelectedCell] = useState<{
    task: Task;
    bucketId: string;
  } | null>(null);

  const isFilterApplied =
    Object.values(
      _pick(
        filterParams,
        TasksFilterFieldNames.Site,
        TasksFilterFieldNames.FormCategory,
        TasksFilterFieldNames.ValidityHints,
      ),
    ).filter((value) => {
      if (Array.isArray(value)) {
        return !!_without(value, '').length;
      }
      return !_isEmpty(value);
    }).length > 0;

  const hasActiveFilter = !!searchQuery || isFilterApplied;

  const resetAllFilters = () => {
    if (searchQuery) setSearchQuery('');
    resetAllFilterParams();
  };

  const openGenerateReportModal = () =>
    useModalManagementResult.openModal(Modals.GenerateReportModal);

  // Actions
  const isGenerateReportModalOpened = useModalManagementResult.isModalOpened(
    Modals.GenerateReportModal,
  );
  const closeGenerateReportModal = () =>
    useModalManagementResult.closeModal(Modals.GenerateReportModal);

  const onToggleFullTableView = () => {
    setFullTableView((prevState) => !prevState);
  };

  const tableColumns = getTimelineTableColumns({
    columns: getAdditionalColumns({
      t,
      view: timelineGroupBy,
      endDateParam,
      onCellClick: (task, bucketId) => {
        logger.info('TasksTimeline: completions opened', {
          taskId: task.id,
          groupKey: task.groupKey,
          bucketId,
        });
        setSelectedCell({ task, bucketId });
      },
    }),
    isFullTableView,
    t,
  });

  // Rows are always scoped to the visible period, so an empty table means
  // "nothing in this period" rather than "no tasks at all".
  const emptyState = hasActiveFilter ? (
    <EmptyState
      title={t('no-results-found')}
      text={t('no-results-found-text')}
      icon={IconName.SearchOff}
      actions={[
        <Button
          key="back"
          buttonText={t('go-back')}
          buttonType={ButtonTypes.primaryFilled}
          leftIcon="arrow_back"
          onClick={resetAllFilters}
        />,
      ]}
    />
  ) : (
    <EmptyState
      title={t('no-completions-for-period')}
      text={t('no-completions-for-period-text')}
      icon={IconName.Cancel}
    />
  );

  return tasksError ? (
    <ErrorPage
      status={getStatusFromError(tasksError)}
      description={getDescriptionFromError(tasksError)}
    />
  ) : (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageHeader title={t('timeline')}>
        {pathname === APP_URLS.dashboard.timeline.path ? (
          <Button
            buttonText={t('generate-report')}
            onClick={openGenerateReportModal}
          />
        ) : undefined}
      </PageHeader>
      <TasksTimelineFilter
        filterParams={filterParams}
        resetFilterParams={resetFilterParams}
        onUpdateFilterParams={onUpdateFilterParams}
        endDate={endDateParam}
        isFullTableView={isFullTableView}
        onToggleFullTableView={onToggleFullTableView}
        onEndDateChange={onEndDateChange}
        onViewChange={onViewChange}
        periodEndParam={periodEndParam}
        periodStartParam={periodStartParam}
        view={view}
      />
      <TasksTimelineStateCounters
        taskCounterData={taskCounterData}
        tasksError={tasksError}
        tasksFirstLoading={tasksFirstLoading}
        filterParams={filterParams}
      />
      <PageBody>
        {tasksFirstLoading ? (
          <CircleLoader />
        ) : (
          <>
            <Search placeholder={t('tasks-timeline-placeholder')} />
            <Table
              columns={tableColumns}
              data={mergeTasksWithTimeline(tasks, timelineData)}
              emptyState={emptyState}
              fullHeight={false}
              id="tasks"
              loading={tasksLoading || timelineLoading}
              orderingState={paginationState}
              paginationState={paginationState}
              totalEntries={totalTasksCount}
            />
          </>
        )}
      </PageBody>
      <Modal
        isOpen={isGenerateReportModalOpened}
        onClose={closeGenerateReportModal}
        title={t('generate-report')}
      >
        <GenerateReportForm
          loading={
            isReportPreparing || taskTimelineReportCreateMutationResult.loading
          }
          onCancel={closeGenerateReportModal}
          graphQLErrors={getGraphQLErrors(
            taskTimelineReportCreateMutationResult.error,
          )}
          onSubmit={onReportCreateAction}
          filterParams={filterParams}
          basePeriodStartParam={basePeriodStartParam}
          basePeriodEndParam={basePeriodEndParam}
          defaultPeriod={reportDefaultPeriod}
        />
      </Modal>
      <TaskCompletionsModal
        task={selectedCell?.task || null}
        bucketId={selectedCell?.bucketId || null}
        onClose={() => setSelectedCell(null)}
      />
    </>
  );
}

export default TasksTimeline;
